import { useRouter } from "next/compat/router";
import posthog from "posthog-js";
import { useEffect, useMemo, useState } from "react";

import usePlanConfig from "./usePlanConfig";
import {
	type JoiningPriceOffer,
	MEMBERSHIP_JOINING_PRICE_FLAG,
	resolveJoiningPrice,
	setCurrentJoiningPriceOffer,
} from "~/lib/membership-joining-price";

/** Ad blockers stop PostHog; show control rather than skeletons forever. */
const FLAG_TIMEOUT_MS = 3000;

let flagValue: unknown;
let flagsReady = false;
let subscribed = false;
const listeners = new Set<() => void>();

const notify = () => {
	for (const listener of listeners) listener();
};

const publishFlag = () => {
	flagValue = posthog.getFeatureFlag(MEMBERSHIP_JOINING_PRICE_FLAG);
	flagsReady = true;
	notify();
};

/**
 * One PostHog subscription shared by every plan card and checkout, so they
 * never disagree on the arm mid-render.
 */
const ensureSubscription = () => {
	if (subscribed || typeof window === "undefined") return;
	subscribed = true;

	posthog.onFeatureFlags(() => {
		publishFlag();
	});

	if (posthog.featureFlags?.hasLoadedFlags) {
		publishFlag();
		return;
	}

	window.setTimeout(() => {
		if (flagsReady) return;
		flagsReady = true;
		notify();
	}, FLAG_TIMEOUT_MS);
};

/**
 * Reads the visitor's joining-price arm. Reading the flag records exposure.
 * In development, `?joining_price=joining_69` previews an arm without PostHog.
 *
 * @param {object} [options]
 * @param {boolean} [options.enabled] - False skips reading the flag, so always-mounted callers don't record exposure
 * @returns {{ offer: JoiningPriceOffer; ready: boolean }} The arm, and whether it is final
 */
const useJoiningPrice = ({
	enabled = true,
}: {
	enabled?: boolean;
} = {}): { offer: JoiningPriceOffer; ready: boolean } => {
	const router = useRouter();
	const { planConfig } = usePlanConfig();
	const [, setVersion] = useState(0);

	useEffect(() => {
		if (!enabled) return;

		const listener = () => setVersion((version) => version + 1);
		listeners.add(listener);
		ensureSubscription();

		return () => {
			listeners.delete(listener);
		};
	}, [enabled]);

	const devQuery = router?.query.joining_price;
	const nodeEnv = process.env.NODE_ENV;
	const hasDevOverride = nodeEnv === "development" && devQuery != null;
	const currency = planConfig.currency;
	const currentFlagValue = flagsReady ? flagValue : undefined;

	const offer = useMemo(
		() =>
			resolveJoiningPrice({
				flagValue: currentFlagValue,
				devQuery,
				nodeEnv,
				currency,
			}),
		[currentFlagValue, devQuery, nodeEnv, currency],
	);

	const ready = enabled && (flagsReady || hasDevOverride);

	useEffect(() => {
		if (ready) setCurrentJoiningPriceOffer(offer);
	}, [ready, offer]);

	return { offer, ready };
};

export default useJoiningPrice;
